/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { SessionErrorResponse, SessionErrorResponseCodeEnum } from 'client-code/session-manager';

import { ErrorTypes, LiveTvError } from '../../error';
import { Locale } from '../../locale/Locale';
import { LocaleKeys } from '../../locale/LocaleKeys';
import { Log } from '../../logging';

/*
    All the playback session realted error from v1/sessionCreate are mapped as a part of 
    LiveTvSessionErrorMapper.
    Example: AuthenticationExpired, NotAuthorizedOutOfHome, TooManyStreams etc.
*/

export class LiveTvSessionErrorMapper {
    private static TAG: string = 'LiveTvSessionErrorMapper';

    private code: string;

    private description: string;

    private message: string;

    private title: string;

    private type: string;

    public constructor() {
        this.code = '';
        this.description = '';
        this.message = '';
        this.title = '';
        this.type = '';
    }

    private setSessionErrorPropertiesByCode(
        errorCode: SessionErrorResponseCodeEnum,
        isNdvrPlayback?: boolean,
    ) {
        switch (errorCode) {
            case SessionErrorResponseCodeEnum.AuthenticationExpired:
                this.type = 'SESSION_AUTHENTICATION_EXPIRED_ERROR';
                this.title = Locale.getString(LocaleKeys.LIVE_TV_APP_SESSION_EXPIRED_TITLE);
                this.description = Locale.getString(LocaleKeys.LIVE_TV_APP_SESSION_EXPIRED_MESSAGE);
                this.code = ErrorTypes.LIVE_TV_SESSION_ERROR_AUTHENTICATION_EXPIRED_ERROR_V409;
                break;
            case SessionErrorResponseCodeEnum.BadArgument:
                this.title = Locale.getString(LocaleKeys.LIVE_TV_CANT_PLAY_TITLE);
                this.type = 'SESSION_BAD_ARGUMENT_ERROR';
                this.description = Locale.getString(LocaleKeys.LIVE_TV_CANT_PLAY_MESSAGE);
                this.code = ErrorTypes.LIVE_TV_SESSION_ERROR_BAD_ARGUMENT_V137;
                break;
            case SessionErrorResponseCodeEnum.BadConfiguration:
                this.type = 'SESSION_BAD_CONFIGURATION_ERROR';
                this.title = Locale.getString(LocaleKeys.LIVE_TV_CANT_PLAY_TITLE);
                this.code = ErrorTypes.LIVE_TV_SESSION_ERROR_BAD_ARGUMENT_V137;
                this.description = Locale.getString(LocaleKeys.LIVE_TV_CANT_PLAY_MESSAGE);
                break;
            case SessionErrorResponseCodeEnum.Deprecated:
                this.title = Locale.getString(LocaleKeys.LIVE_TV_CANT_PLAY_TITLE);
                this.type = 'SESSION_DEPRECATED_ERROR';
                this.code = ErrorTypes.LIVE_TV_SESSION_ERROR_BAD_ARGUMENT_V137;
                this.description = Locale.getString(LocaleKeys.LIVE_TV_CANT_PLAY_MESSAGE);
                break;
            case SessionErrorResponseCodeEnum.ExternalError:
                this.title = Locale.getString(LocaleKeys.LIVE_TV_CANT_PLAY_TITLE);
                this.description = Locale.getString(LocaleKeys.LIVE_TV_CANT_PLAY_MESSAGE);
                this.code = ErrorTypes.LIVE_TV_SESSION_ERROR_BAD_ARGUMENT_V137;
                this.type = 'SESSION_EXTERNAL_ERROR';
                break;
            case SessionErrorResponseCodeEnum.HostUnavailable:
                this.title = Locale.getString(LocaleKeys.LIVE_TV_CANT_PLAY_TITLE);
                this.code = ErrorTypes.LIVE_TV_SESSION_ERROR_BAD_ARGUMENT_V137;
                this.description = Locale.getString(LocaleKeys.LIVE_TV_CANT_PLAY_MESSAGE);
                this.type = 'SESSION_HOST_UNAVAILABLE_ERROR';
                break;
            case SessionErrorResponseCodeEnum.InternalError:
                this.type = 'SESSION_INTERNAL_ERROR';
                this.title = Locale.getString(LocaleKeys.LIVE_TV_CANT_PLAY_TITLE);
                this.description = Locale.getString(LocaleKeys.LIVE_TV_CANT_PLAY_MESSAGE);
                this.code = ErrorTypes.LIVE_TV_SESSION_ERROR_INTERNAL_ERROR_V138;
                break;
            case SessionErrorResponseCodeEnum.NoSession:
                this.type = 'SESSION_NO_SESSION_ERROR';
                this.title = Locale.getString(LocaleKeys.LIVE_TV_CANT_PLAY_TITLE);
                this.description = Locale.getString(LocaleKeys.LIVE_TV_NO_SESSION_MESSAGE);
                this.code = ErrorTypes.LIVE_TV_SESSION_ERROR_NO_SESSION_V922;
                break;
            case SessionErrorResponseCodeEnum.NotAuthenticated:
                this.type = 'SESSION_NOT_AUTHENTICATED_ERROR';
                this.title = Locale.getString(LocaleKeys.LIVE_TV_SOMETHING_WENT_WRONG_TITLE);
                this.description = Locale.getString(
                    LocaleKeys.LIVE_TV_SOMETHING_WENT_WRONG_MESSAGE,
                );
                this.code = ErrorTypes.LIVE_TV_SESSION_ERROR_NOT_AUTHENTICATED_V403;
                break;
            case SessionErrorResponseCodeEnum.NotAuthorized:
                this.type = 'SESSION_NOT_AUTHORIZED_ERROR';
                this.title = Locale.getString(LocaleKeys.LIVE_TV_SUBSCRIPTION_REQUIRED_TITLE);
                this.description = isNdvrPlayback
                    ? Locale.getString(LocaleKeys.NDVR_SESSION_ERROR_NOT_AUTHORIZED_MESSAGE)
                    : Locale.getString(LocaleKeys.LIVE_TV_SUBSCRIPTION_REQUIRED_MESSAGE);
                this.code = isNdvrPlayback
                    ? ErrorTypes.NDVR_SESSION_ERROR_NOT_AUTHORIZED_V400
                    : ErrorTypes.LIVE_TV_SESSION_ERROR_NOT_AUTHORIZED_V410;
                break;
            case SessionErrorResponseCodeEnum.NotAuthorizedDeviceType:
                this.type = 'SESSION_NOT_AUTHORIZED_DEVICE_TYPE_ERROR';
                this.code = isNdvrPlayback
                    ? ErrorTypes.NDVR_SESSION_ERROR_NOT_AUTHORIZED_V400
                    : ErrorTypes.LIVE_TV_SESSION_ERROR_NOT_AUTHORIZED_DEVICE_TYPE_V414;
                this.description = isNdvrPlayback
                    ? Locale.getString(LocaleKeys.NDVR_SESSION_ERROR_NOT_AUTHORIZED_MESSAGE)
                    : Locale.getString(LocaleKeys.LIVE_TV_CANNOT_PLAY_ON_THIS_DEVICE_OSD_MESSAGE);
                break;
            case SessionErrorResponseCodeEnum.NotAuthorizedOutOfHome:
                this.type = 'SESSION_NOT_AUTHORIZED_OUT_OF_HOME_ERROR';
                this.code = ErrorTypes.LIVE_TV_OOH_OSD_ERROR_V404;
                this.description = Locale.getString(LocaleKeys.LIVE_TV_OOH_OSD_MESSAGE);
                break;
            case SessionErrorResponseCodeEnum.NotAuthorizedOutOfRegion:
                this.type = 'SESSION_NOT_AUTHORIZED_OUT_OF_REGION_ERROR';
                this.code = ErrorTypes.LIVE_TV_OUT_OF_REGION_OSD_ERROR_V405;
                this.description = Locale.getString(LocaleKeys.LIVE_TV_OUT_OF_REGION_OSD_MESSAGE);
                break;
            case SessionErrorResponseCodeEnum.NotRegistered:
                this.type = 'SESSION_NOT_REGISTERED_ERROR';
                this.title = Locale.getString(LocaleKeys.LIVE_TV_CANT_WATCH_SHOWS_TITLE);
                this.description = Locale.getString(LocaleKeys.LIVE_TV_CANT_WATCH_SHOWS_MESSAGE);
                this.code = ErrorTypes.LIVE_TV_SESSION_ERROR_NOT_REGISTERED_V406;
                break;
            case SessionErrorResponseCodeEnum.TooManyStreams:
                this.type = 'SESSION_TOO_MANY_STREAMS_ERROR';
                this.title = Locale.getString(LocaleKeys.LIVE_TV_CANT_WATCH_NOW_TITLE);
                this.description = Locale.getString(LocaleKeys.LIVE_TV_CANT_WATCH_NOW_MESSAGE);
                this.code = ErrorTypes.LIVE_TV_SESSION_ERROR_TOO_MANY_STREAMS_V407;
                break;
            case SessionErrorResponseCodeEnum.TooManyStreamsForResource:
                this.type = 'SESSION_TOO_MANY_STREAMS_FOR_RESOURCE_ERROR';
                this.title = Locale.getString(LocaleKeys.LIVE_TV_CANT_WATCH_NOW_TITLE);
                this.description = Locale.getString(
                    LocaleKeys.LIVE_TV_TOO_MANY_STREAMS_FOR_RESOURCE_MESSAGE,
                );
                this.code =
                    ErrorTypes.LIVE_TV_SESSION_ERROR_TOO_MANY_STREAMS_FOR_RESOURCE_ERROR_V408;
                break;
            case SessionErrorResponseCodeEnum.UnknownLocation:
                this.type = 'SESSION_UNKNOWN_LOCATION_ERROR';
                this.code = ErrorTypes.LIVE_TV_OUT_OF_REGION_OSD_ERROR_V405;
                this.description = Locale.getString(LocaleKeys.LIVE_TV_OUT_OF_REGION_OSD_MESSAGE);
                break;
            default:
                this.type = 'SESSION_UNKNOWN_ERROR';
                this.title = Locale.getString(LocaleKeys.LIVE_TV_CANT_PLAY_TITLE);
                this.description = Locale.getString(LocaleKeys.LIVE_TV_UNABLE_TO_PLAY_MESSAGE);
                this.code = isNdvrPlayback
                    ? ErrorTypes.NDVR_SESSION_ERROR_DEFAULT_V854
                    : ErrorTypes.LIVE_TV_SESSION_ERROR_UNKNOWN_ERROR_V603;
                break;
        }
    }

    private setSessionErrorPropertiesByError(
        error: SessionErrorResponse,
        isNdvrPlayback?: boolean,
    ) {
        Log.e(LiveTvSessionErrorMapper.TAG, 'session error ', error);
        Diagnostics.error(LiveTvSessionErrorMapper.TAG, 'session error ', error);
        const errorCode = error?.code || '';
        this.message = error?.message || '';
        this.setSessionErrorPropertiesByCode(errorCode, isNdvrPlayback);
    }

    public getErrorByCode(errorCode: SessionErrorResponseCodeEnum): LiveTvError {
        Log.e(LiveTvSessionErrorMapper.TAG, 'session error code ', errorCode);
        Diagnostics.error(LiveTvSessionErrorMapper.TAG, 'session error code ', errorCode);
        this.setSessionErrorPropertiesByCode(errorCode);
        return new LiveTvError(this.code, this.message, this.description, this.title, this.type);
    }

    public getErrorByErrorObject(
        error: SessionErrorResponse,
        isNdvrPlayback?: boolean,
    ): LiveTvError {
        this.setSessionErrorPropertiesByError(error, isNdvrPlayback);
        return new LiveTvError(this.code, this.message, this.description, this.title, this.type);
    }
}
